import React from 'react';
import { motion } from 'framer-motion';
import {
  FaPlay,
  FaGem,
  FaStar,
  FaAward,
  FaUsers,
  FaGraduationCap,
  FaPalette,
  FaPenFancy,
  FaBriefcase
} from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import { useNavigate } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/pagination';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();

  // Sample jewelry images - these would be dynamic in a real application
  const jewelryImages = [
    {
      id: 1,
      src: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=400&h=400&fit=crop',
      title: 'Elegant Diamond Ring',
      description: 'Handcrafted with precision and passion'
    },
    {
      id: 2,
      src: 'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400&h=400&fit=crop',
      title: 'Golden Necklace',
      description: 'Timeless beauty meets modern design'
    },
    {
      id: 3,
      src: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400&h=400&fit=crop',
      title: 'Pearl Earrings',
      description: 'Classic elegance for every occasion'
    },
    {
      id: 4,
      src: 'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=400&h=400&fit=crop',
      title: 'Emerald Bracelet',
      description: 'Nature\'s beauty captured in precious metal'
    },
    {
      id: 5,
      src: 'https://images.unsplash.com/photo-1617038220319-276d4c4b8b6a?w=400&h=400&fit=crop',
      title: 'Sapphire Pendant',
      description: 'Deep blue mystery in elegant form'
    },
    {
      id: 6,
      src: 'https://images.unsplash.com/photo-1603561596112-db0b0b5b0b5b?w=400&h=400&fit=crop',
      title: 'Ruby Brooch',
      description: 'Bold statement piece for special moments'
    }
  ];

  const stats = [
    { icon: <FaUsers />, number: '50+', label: 'Students Trained' },
    { icon: <FaGraduationCap />, number: '5+', label: 'Years Experience' },
    { icon: <FaAward />, number: '5+', label: 'Awards Won' },
    { icon: <FaStar />, number: '98%', label: 'Success Rate' }
  ];

  const serviceCards = [
    {
      icon: <FaPalette />,
      title: 'Design & craft',
      body: 'Manual and CAD jewellery design with a focus on proportion, wearability, and client-ready presentation.',
      action: () => navigate('/courses')
    },
    {
      icon: <FaPenFancy />,
      title: 'Digital sketching',
      body: 'Concept development and digital workflows—including Procreate—to move from idea to polished visuals.',
      action: () => navigate('/courses')
    },
    {
      icon: <FaBriefcase />,
      title: 'Business & brand',
      body: 'Entrepreneurship, branding, and industry context so your creativity connects with real customers.',
      action: () => navigate('/contact')
    }
  ];

  return (
    <div className="homepage">
      <section className="hero-section">
        <div className="hero-silk" aria-hidden />
        <div className="hero-background">
          <div className="jewelry-animation">
            {[...Array(14)].map((_, i) => (
              <motion.div
                key={i}
                className="floating-jewel"
                style={{
                  left: `${(i * 7.3) % 100}%`,
                  top: `${(i * 11) % 85}%`,
                  animationDelay: `${(i % 5) * 0.4}s`,
                  animationDuration: `${3.2 + (i % 3) * 0.4}s`
                }}
                animate={{
                  y: [0, -14, 0],
                  rotate: [0, 120, 240],
                  opacity: [0.2, 0.55, 0.2]
                }}
                transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }}
              >
                <FaGem />
              </motion.div>
            ))}
          </div>
        </div>

        <div className="container hero-layout">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85 }}
          >
            <p className="hero-eyebrow">GL Jewel Academy</p>
            <h1 className="hero-title">
              <span className="hero-title-serif">Master the art of</span>
              <span className="hero-title-script">Jewellery design</span>
            </h1>
            <p className="hero-subtitle">
              Learn from industry expert Nancy Johar. Blend craftsmanship, digital tools, and
              business sense—so your passion becomes a profession.
            </p>
            <div className="hero-buttons">
              <motion.button
                type="button"
                className="btn btn-primary btn-lg"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/courses')}
              >
                Explore courses
              </motion.button>
              <motion.button
                type="button"
                className="btn btn-outline btn-lg"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaPlay className="play-icon" aria-hidden />
                Watch demo
              </motion.button>
            </div>
          </motion.div>
          <div className="hero-visual" aria-hidden>
            <div className="hero-botanical hero-botanical--left" />
            <div className="hero-showcase">
              <div className="hero-pedestal" />
              <div className="hero-orb" />
            </div>
            <div className="hero-botanical hero-botanical--right" />
          </div>
        </div>
      </section>

      <section className="services-section section-light">
        <div className="container">
          <header className="section-heading">
            <h2 className="section-heading-title">Our programmes</h2>
            <p className="section-heading-sub">
              Structured paths in design, digital craft, and business—aligned with how the industry works today.
            </p>
          </header>
          <div className="services-grid">
            {serviceCards.map((card) => (
              <article key={card.title} className="service-card">
                <div className="service-card-icon" aria-hidden>
                  {card.icon}
                </div>
                <h3 className="service-card-title">{card.title}</h3>
                <p className="service-card-body">{card.body}</p>
                <button type="button" className="btn btn-primary btn-sm service-card-cta" onClick={card.action}>
                  Learn more
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="video-section section-light why-choose-section">
        <div className="container">
          <header className="section-heading">
            <h2 className="section-heading-title">Why choose us</h2>
            <p className="section-heading-sub">
              Personal attention, real industry exposure, and a curriculum built for portfolios that open doors.
            </p>
          </header>
          <motion.div
            className="video-container why-choose-grid"
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.75 }}
            viewport={{ once: true }}
          >
            <aside className="why-cards" aria-label="Highlights">
              <div className="why-mini-card">
                <FaAward className="why-mini-icon" aria-hidden />
                <h3 className="why-mini-title">Expert-led training</h3>
                <p className="why-mini-text">
                  Learn from educators with deep industry certifications and research-backed insight.
                </p>
              </div>
              <div className="why-mini-card">
                <FaUsers className="why-mini-icon" aria-hidden />
                <h3 className="why-mini-title">Small cohorts</h3>
                <p className="why-mini-text">
                  Room for feedback, critique, and mentorship at every stage of your projects.
                </p>
              </div>
            </aside>
            <div className="why-choose-media">
              <div className="video-wrapper video-wrapper--laptop">
                <div className="video-placeholder">
                  <FaPlay className="play-button" />
                  <p>Course introduction video</p>
                </div>
              </div>
              <div className="video-info">
                <h2 className="video-info-heading">Discover your creative potential</h2>
                <p>
                  Watch our introduction to see how we combine traditional craft, CAD, and digital
                  presentation—so you graduate with work you are proud to show.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Jewelry Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={0}
            slidesPerView={1}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            speed={1000}
            loop={true}
            pagination={{ 
              clickable: true
            }}
            className="jewelry-swiper"
          >
            {jewelryImages.map((jewelry) => (
              <SwiperSlide key={jewelry.id}>
                <div className="jewelry-slide">
                  <img 
                    src={jewelry.src} 
                    alt={jewelry.title}
                    className="jewelry-image"
                  />
                  <div className="jewelry-overlay">
                    <h3>{jewelry.title}</h3>
                    <p>{jewelry.description}</p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* About Course Section */}
      <section className="about-course-section section-light">
        <div className="container">
          <div className="about-content">
            <motion.div
              className="about-text"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>About Our Jewelry Design Course</h2>
              <p>
                Our comprehensive jewelry design program combines traditional craftsmanship 
                with modern techniques, providing students with the skills and knowledge 
                needed to create exceptional pieces. From basic design principles to 
                advanced metalworking techniques, our curriculum covers everything you 
                need to succeed in the jewelry industry.
              </p>
              <div className="course-features">
                <div className="feature">
                  <FaGem className="feature-icon" />
                  <div>
                    <h4>Hands-on Learning</h4>
                    <p>Practical experience with real tools and materials</p>
                  </div>
                </div>
                <div className="feature">
                  <FaAward className="feature-icon" />
                  <div>
                    <h4>Expert Instruction</h4>
                    <p>Learn from industry professionals with decades of experience</p>
                  </div>
                </div>
                <div className="feature">
                  <FaUsers className="feature-icon" />
                  <div>
                    <h4>Small Class Sizes</h4>
                    <p>Personalized attention and guidance for every student</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="stats-container"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3>Our Success Stories</h3>
              <div className="stats-grid">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    className="stat-item"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="about-us-section section-gold">
        <div className="container">
          {/* Our Journey Section */}
          <motion.div
            className="journey-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>OUR JOURNEY</h2>
            <p>
              GL Jewel Academy is the educational extension of the legacy of Kapil 
              Jewellers, founded by Mr. Gopal Kumar Johar in 1990. With a reputation 
              for quality, trust, and customer satisfaction, Kapil Jewellers has thrived for 
              over three decades.
            </p>
            <p>
              Carrying forward this excellence, GL Jewel Academy – By Nancy Johar 
              was established to offer professional jewellery education rooted in 
              innovation, practicality, and global standards. The academy proudly 
              collaborates with Kapil Jewellers to provide its students with real industry 
              exposure.
            </p>
          </motion.div>

          {/* Our Mission & Specialization Section */}
          <motion.div
            className="mission-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h2>OUR MISSION & SPECIALIZATION</h2>
            <blockquote className="mission-quote">
              "Our mission is to spread knowledge that creates a global impact—by 
              empowering professionals with the skills to shine in the jewellery 
              industry."
            </blockquote>
            <div className="specialization-list">
              <h3>We specialise in:</h3>
              <ul>
                <li>Manual & CAD jewellery design</li>
                <li>Digital design with Procreate</li>
                <li>Customer-centric jewellery sketching & concept development</li>
                <li>Business & brand building for jewellery entrepreneurs</li>
                <li>Training that blends creativity + professionalism + real-world skills</li>
              </ul>
            </div>
          </motion.div>

          {/* Founders Section */}
          <motion.div
            className="founders-section"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h2>Meet Our Founders</h2>
            
            {/* Nancy Johar Section */}
            <div className="founder-section-individual">
              <div className="founder-header">
                <h3>Nancy Johar</h3>
                <h4>• Founder & Director – GL Jewel Academy</h4>
              </div>
              
              <div className="founder-content-wrapper">
                <div className="founder-image-container">
                  <img 
                    src="/images/nancy_johar_profile.JPG" 
                    alt="Nancy Johar" 
                    className="founder-profile-img"
                  />
                  <div className="founder-badge">
                    <FaAward />
                    <span>Founder & Director</span>
                  </div>
                </div>
                
                <div className="founder-details">
                  <div className="founder-bio">
                    <p>
                      A visionary leader and skilled educator, Nancy Johar blends academic 
                      excellence with deep industry knowledge. After completing her Bachelor's 
                      in Commerce from Delhi University and Post-Graduation in Management 
                      with specialisation in Big Data Analytics, she began her career as a Data 
                      Analyst in the corporate world.
                    </p>
                    <p>
                      Driven by her passion for jewellery and a desire to carry forward her 
                      father's legacy in the industry, she pursued extensive professional training 
                      in jewellery. Nancy holds professional certifications in Manual Design, 
                      CAD Design, and Digital Design through Procreate, and has been 
                      trained by some of the most reputable names in the industry:
                    </p>
                  </div>
                  
                  <div className="certifications-section">
                    <h5>Professional Certifications:</h5>
                    <ul className="certifications-list">
                      <li>International Institute of Gemology</li>
                      <li>Business of Jewellery – De Beers Forevermark</li>
                      <li>Lab-Grown Diamonds – Limelight</li>
                      <li>Contemporary Design – Outhouse</li>
                      <li>Fine Jewellery – Anmol Jewellers</li>
                      <li>Heritage Jewellery – Amrapali by Tarang Arora</li>
                    </ul>
                  </div>
                  
                  <div className="research-section">
                    <h5>Current Research & Publications:</h5>
                    <p>
                      Currently pursuing a PhD in Management, Nancy is researching the 
                      evolving dynamics of the jewellery industry. She has presented her work 
                      internationally and nationally, including her research papers:
                    </p>
                    <ul className="research-papers">
                      <li>"Strategic Management of Lab-Grown Diamonds: Navigating Market 
                      Disruption, Sustainability, and Consumer Preferences."</li>
                      <li>"De Beers and the Lab-grown Diamonds Dilemma: Strategic Transitions 
                      and Industry Dynamics"</li>
                      <li>"Global Dynamics and Implications for Indian Gold Prices: An 
                      Economic Perspective"</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Nikhil Johar Section */}
            <div className="founder-section-individual">
              <div className="founder-header">
                <h3>Nikhil Johar</h3>
                <h4>• Co-Founder – GL Jewel Academy</h4>
              </div>
              
              <div className="founder-content-wrapper">
                <div className="founder-image-container">
                  <img 
                    src="/images/nikhil_profile.jpg" 
                    alt="Nikhil Johar" 
                    className="founder-profile-img"
                  />
                  <div className="founder-badge">
                    <FaUsers />
                    <span>Co-Founder</span>
                  </div>
                </div>
                
                <div className="founder-details">
                  <div className="founder-bio">
                    <p>
                      With over 10 years of experience in the jewellery industry, Nikhil Johar 
                      has built his expertise through education and practice. A Commerce (Hons) 
                      graduate and then post-graduate with a management specialisation in 
                      International Business, he has worked extensively with Kapil Jewellers—
                      a family-owned jewellery business founded in 1990.
                    </p>
                    <p>
                      Nikhil brings real-world insights, operational knowledge, and market 
                      awareness to the academy's training modules, ensuring students learn not 
                      only design but also business dynamics.
                    </p>
                  </div>
                  
                  <div className="experience-section">
                    <h5>Industry Experience:</h5>
                    <ul className="experience-list">
                      <li>10+ years in jewellery industry operations</li>
                      <li>Extensive work with Kapil Jewellers (established 1990)</li>
                      <li>Commerce (Hons) graduate</li>
                      <li>Post-graduate in Management with International Business specialization</li>
                      <li>Expertise in business dynamics and market awareness</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="finale-cta-section" aria-labelledby="finale-cta-heading">
        <div className="finale-silk" aria-hidden />
        <div className="container finale-cta-inner">
          <h2 id="finale-cta-heading" className="finale-cta-title">
            Ready to transform your craft?
          </h2>
          <p className="finale-cta-sub">
            Book a conversation about courses, schedules, and how we can support your goals.
          </p>
          <motion.button
            type="button"
            className="btn btn-primary btn-lg finale-cta-btn"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/contact')}
          >
            Get a free consultation
          </motion.button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
